import apiClient from './apiClient';

const MEKAK_PREFIX = '/api/mekak';

// Types
export interface Knowledge {
  id: string;
  title: string;
  content: string;
  author: string;
  tags: string[];
  view_count: number;
  llm_tokens: number;
  embedding_chars: number;
  total_cost: number;
  metadata?: KnowledgeMetadata;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeMetadata {
  preprocessed: boolean;
  chunk_count: number;
  vector_ids: string[];
  main_topic: string;
  potential_queries: string[];
  llm_tokens: number;
  embedding_chars: number;
  total_cost: number;
}

export interface KnowledgeCreateRequest {
  title?: string;
  content: string;
  author: string;
  tags: string[];
}

export interface SearchRequest {
  query: string;
  top_k?: number;
  category?: string;
  force_mode?: 'pure_sql' | 'mixed';
  enable_mixed_search?: boolean;
  enhance_query?: boolean;
  user_id?: string;
}

// Legacy format (kept for backward compatibility)
export interface SearchResult {
  answer: string;
  sources: Array<{
    id: string;
    content: string;
    similarity: number;
    title?: string;
  }>;
  total_tokens?: number;
  estimated_cost?: number;
}

// New advanced search response
export interface SQLAnswer {
  id?: string;
  query_id: string;
  query_text: string;
  sql_query: string;
  table_name: string;
  row_count: number;
  results: Record<string, unknown>[];
  execution_time_ms: number;
  created_at?: string;
  column_aliases?: Record<string, string>; // column_name → 中文顯示名
  original_filename?: string; // 原始 CSV 檔名
}

export interface LLMAnswer {
  id?: string;
  query_id: string;
  query_text: string;
  answer_type: string;
  answer_content: string;
  source_type: string;
  confidence: number;
  metadata?: unknown;
  created_at?: string;
}

export interface RelatedImage {
  id: string;
  gcs_url: string;
  mime_type: string;
  page_number: number;
  width: number;
  height: number;
  ocr_text?: string;
  description?: string;
  source_file?: string;
}

export interface AdvancedSearchResponse {
  success: boolean;
  query_id: string;
  query: string;
  query_mode: 'pure_sql' | 'mixed';
  answer: string;
  total_results: number;
  processing_ms: number;
  tokens_used: number;
  sources_count: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  estimated_cost: number;
  model_used: string;
  llm_answers: LLMAnswer[];
  sql_answers: SQLAnswer[];
  related_images?: RelatedImage[];
  data_results?: unknown[];
  decision_details?: {
    mode_decision: {
      input: string;
      decision: string;
      analysis: string;
      reason: string;
    };
    query_decomposition?: {
      vector_part: string;
      sql_part: string;
    };
  };
}

export interface QueryRecord {
  id: string;
  user_id: string;
  username: string;
  query: string;
  answer: string;
  total_tokens: number;
  estimated_cost: number;
  model_used: string;
  created_at: string;
  related_images?: RelatedImage[];
}

export interface DashboardStats {
  knowledge: {
    total_count: number;
    total_views: number;
    recent_count: number;
    top_tags: Array<{ tag: string; count: number }>;
  };
  system: {
    total_tokens: number;
    total_cost: number;
    total_queries: number;
    daily_stats: Array<{
      date: string;
      tokens: number;
      cost: number;
      queries: number;
    }>;
    query_records: QueryRecord[];
  };
}

export interface CSVFile {
  id: string;
  filename: string;
  row_count: number;
  processed: boolean;
  vector_generated: boolean;
  uploaded_at: string;
  created_at: string;
}

export interface CSVColumnDetail {
  column_name: string;
  column_index: number;
  data_type: string;
  semantic_type: string;
  chinese_name: string;
  aliases: string[];
  distinct_count: number;
  null_count: number;
  min_value: string;
  max_value: string;
  sample_values: string[];
}

export interface CSVFileDetail {
  id: string;
  filename: string;
  row_count: number;
  processed: boolean;
  vector_generated: boolean;
  uploaded_at: string;
  table_name: string;
  display_name: string;
  column_count: number;
  business_domain: string;
  vectorization_status: string;
  vectorization_progress: number;
  total_vectors_count: number;
  columns: CSVColumnDetail[];
  sample_data: Record<string, unknown>[];
}

export interface CSVUploadResult {
  filename: string;
  rows_processed: number;
  vectors_created: number;
}

// Knowledge Folder types
export interface KnowledgeFolder {
  id: string;
  name: string;
  parent_id: string | null;
  path: string;
  sort_order: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface FolderTreeNode {
  id: string;
  name: string;
  parent_id: string | null;
  path: string;
  sort_order: number;
  children: FolderTreeNode[];
  knowledge_count: number;
  file_document_count: number;
}

export interface FolderContentsResponse {
  folder: KnowledgeFolder | null;
  breadcrumb: KnowledgeFolder[];
  sub_folders: FolderTreeNode[];
  knowledge: Knowledge[];
  file_documents: FileDocument[];
  total_knowledge: number;
  total_file_documents: number;
}

// File Document types (PPT/PDF upload)
export interface FileDocument {
  id: string;
  original_filename: string;
  file_type: string;
  file_size_bytes: number;
  page_count: number;
  knowledge_count: number;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  processing_error?: string;
  author: string;
  user_id: string;
  llm_tokens: number;
  embedding_chars: number;
  total_cost: number;
  created_at: string;
  updated_at: string;
}

export interface FileProcessResult {
  file_document_id: string;
  original_name: string;
  file_type: string;
  file_size_bytes: number;
  page_count: number;
  knowledge_count: number;
  merged_blocks: Array<{
    content: string;
    page_numbers: number[];
  }>;
}

export interface KnowledgeImage {
  id: string;
  file_document_id: string;
  knowledge_id?: string;
  page_number: number;
  image_index: number;
  gcs_url: string;
  mime_type: string;
  width: number;
  height: number;
  content_hash?: string;
  ocr_text?: string;
  description?: string;
  file_size_bytes: number;
  created_at: string;
}

export interface FileDocumentDetail {
  file_document: FileDocument;
  knowledge: Array<{
    id: string;
    title: string;
    content: string;
    author: string;
    source_type: string;
    page_numbers: number[];
    created_at: string;
  }>;
  images: KnowledgeImage[];
}

// API functions
export const mekakApi = {
  // Knowledge CRUD
  listKnowledge: (params?: { page?: number; limit?: number; search?: string; tag?: string }) =>
    apiClient.get<{ success: boolean; data: Knowledge[]; total?: number }>(`${MEKAK_PREFIX}/knowledge`, { params }),

  getKnowledge: (id: string) =>
    apiClient.get<{ success: boolean; data: Knowledge }>(`${MEKAK_PREFIX}/knowledge/${id}`),

  createKnowledge: (data: KnowledgeCreateRequest) =>
    apiClient.post<{ success: boolean; data: Knowledge; message?: string }>(`${MEKAK_PREFIX}/knowledge`, data),

  updateKnowledge: (id: string, data: Partial<KnowledgeCreateRequest>) =>
    apiClient.put<{ success: boolean; data: Knowledge }>(`${MEKAK_PREFIX}/knowledge/${id}`, data),

  deleteKnowledge: (id: string) =>
    apiClient.delete<{ success: boolean }>(`${MEKAK_PREFIX}/knowledge/${id}`),

  // Knowledge Folders
  getFolderTree: () =>
    apiClient.get<{ success: boolean; data: FolderTreeNode[] }>(`${MEKAK_PREFIX}/folders/tree`),

  getFolderContents: (params?: { folder_id?: string; limit?: number; offset?: number }) =>
    apiClient.get<{ success: boolean; data: FolderContentsResponse }>(`${MEKAK_PREFIX}/folders/contents`, { params }),

  createFolder: (data: { name: string; parent_id?: string | null }) =>
    apiClient.post<{ success: boolean; data: KnowledgeFolder }>(`${MEKAK_PREFIX}/folders`, data),

  updateFolder: (id: string, data: { name: string }) =>
    apiClient.put<{ success: boolean }>(`${MEKAK_PREFIX}/folders/${id}`, data),

  moveFolder: (id: string, data: { parent_id: string | null }) =>
    apiClient.put<{ success: boolean }>(`${MEKAK_PREFIX}/folders/${id}/move`, data),

  deleteFolder: (id: string) =>
    apiClient.delete<{ success: boolean }>(`${MEKAK_PREFIX}/folders/${id}`),

  moveItemsToFolder: (data: { knowledge_ids?: string[]; file_doc_ids?: string[]; folder_id: string | null }) =>
    apiClient.put<{ success: boolean }>(`${MEKAK_PREFIX}/items/move`, data),

  // AI Search (90s timeout for AI pipeline)
  search: (data: SearchRequest) =>
    apiClient.post<{ success: boolean; data: AdvancedSearchResponse }>(`${MEKAK_PREFIX}/query`, data, {
      timeout: 90000,
    }),

  getHistory: () =>
    apiClient.get<{ success: boolean; data: QueryRecord[] }>(`${MEKAK_PREFIX}/query/history`),

  // CSV Upload
  uploadCSV: (formData: FormData) =>
    apiClient.post<{ success: boolean; data: CSVUploadResult; message?: string }>(`${MEKAK_PREFIX}/csv/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    }),

  listCSVFiles: () =>
    apiClient.get<{ success: boolean; data: CSVFile[] }>(`${MEKAK_PREFIX}/csv/files`),

  getCSVFileDetail: (id: string) =>
    apiClient.get<{ success: boolean; data: CSVFileDetail }>(`${MEKAK_PREFIX}/csv/files/${id}`),

  deleteCSVFile: (id: string) =>
    apiClient.delete<{ success: boolean; message?: string }>(`${MEKAK_PREFIX}/csv/files/${id}`),

  revectorizeCSVFile: (id: string) =>
    apiClient.post<{ success: boolean; message?: string }>(`${MEKAK_PREFIX}/csv/revectorize/${id}`),

  downloadAllCSV: () =>
    apiClient.get(`${MEKAK_PREFIX}/csv/download-all`, { responseType: 'blob', timeout: 300000 }),

  // File Upload (PPT/PDF) — legacy direct upload, ≤32MB only (App Engine limit).
  // For larger files use createUploadURL + (PUT to signed URL) + startFileProcess.
  uploadFile: (formData: FormData) =>
    apiClient.post<{ success: boolean; data: FileProcessResult; message?: string }>(`${MEKAK_PREFIX}/files/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    }),

  // Large-file upload: get a V4 signed URL for direct PUT to GCS.
  createUploadURL: (params: { filename: string; content_type: string; file_size: number; folder_id?: string | null }) =>
    apiClient.post<{
      success: boolean;
      data: { file_doc_id: string; upload_url: string; gcs_path: string; content_type: string; expires_in: number };
      error?: string;
    }>(`${MEKAK_PREFIX}/files/upload-url`, {
      filename: params.filename,
      content_type: params.content_type,
      file_size: params.file_size,
      folder_id: params.folder_id || '',
    }),

  // Notify the backend that the GCS PUT completed; processing kicks off in the background.
  startFileProcess: (fileDocID: string) =>
    apiClient.post<{ success: boolean; data: { file_doc_id: string; status: string }; error?: string }>(
      `${MEKAK_PREFIX}/files/${fileDocID}/process`,
    ),

  listFileDocuments: (params?: { limit?: number; offset?: number }) =>
    apiClient.get<{ success: boolean; data: FileDocument[]; count: number }>(`${MEKAK_PREFIX}/files`, { params }),

  getFileDocumentDetail: (id: string) =>
    apiClient.get<{ success: boolean; data: FileDocumentDetail }>(`${MEKAK_PREFIX}/files/${id}`),

  deleteFileDocument: (id: string) =>
    apiClient.delete<{ success: boolean; message?: string }>(`${MEKAK_PREFIX}/files/${id}`),

  getKnowledgeImages: (knowledgeId: string) =>
    apiClient.get<{ success: boolean; data: KnowledgeImage[] }>(`${MEKAK_PREFIX}/knowledge/${knowledgeId}/images`),

  updateImageDescription: (imageId: string, description: string) =>
    apiClient.put<{ success: boolean; message?: string }>(`${MEKAK_PREFIX}/images/${imageId}/description`, { description }),

  // Stats
  getDashboard: () =>
    apiClient.get<{ success: boolean; data: DashboardStats }>(`${MEKAK_PREFIX}/stats/dashboard`),

  getTokenUsage: () =>
    apiClient.get<{ success: boolean; data: any }>(`${MEKAK_PREFIX}/stats/tokens`),
};

export default mekakApi;
